import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mailbox, Mail, CheckCircle } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Seo } from "@/components/site/Seo";
import { CONTACT } from "@/data/site";

const SERVICES = [
  {
    title: "Domiciliazione postale o recapito commerciale",
    body: "Ideale se hai bisogno di un indirizzo civico da inserire a soli fini di rappresentanza.",
  },
  {
    title: "Domiciliazione legale",
    body: "Sede legale di società di capitali e cooperative da costituire o già esistenti.",
  },
  {
    title: "Domiciliazione professionale",
    body: "Domicilio professionale per avvocati, commercialisti, esperti contabili, ingegneri.",
  },
  {
    title: "Domiciliazione di ditta individuale",
    body: "La ditta che nasce dalla volontà di una persona fisica di intraprendere un'attività economica.",
  },
];

export default function Domiciliazione() {
  return (
    <div data-testid="domiciliazione-page">
      <Seo
        title="Domiciliazione a Frosinone — legale, postale e professionale | treffe"
        description="Domiciliazione postale, legale, professionale e per ditta individuale a Frosinone: la sede treffe in Viale Tevere 8/A è la location ideale per domiciliare la tua attività."
        path="/domiciliazione"
      />
      <PageHeader
        eyebrow="Domiciliazione"
        title={<>Domicilia la tua attività nella nostra sede.</>}
        subtitle="Il nostro business center, per la posizione centrale, la professionalità del personale e l'immagine che ci caratterizza, è una location ideale per domiciliare la tua attività."
      />

      <section className="py-16 md:py-24 bg-white border-b border-border" data-testid="domiciliazione-services">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">Quattro servizi, una scelta chiara</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter text-brand-black">
              Quale domiciliazione fa per te.
            </h2>
            <p className="mt-4 text-brand-ink text-base md:text-lg leading-relaxed">
              È necessario distinguere i servizi di domiciliazione, per fare chiarezza e aiutarti nella scelta del servizio migliore.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {SERVICES.map((s, i) => (
              <motion.article
                key={s.title}
                data-testid={`domiciliazione-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="bg-brand-paper border border-border p-6 md:p-7 hover:border-brand-red transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 shrink-0 bg-brand-black text-white flex items-center justify-center font-heading font-bold text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-heading font-bold text-lg md:text-xl text-brand-black leading-tight">{s.title}</h3>
                </div>
                <p className="text-sm md:text-base text-brand-ink leading-relaxed">{s.body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-brand-paper">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="bg-brand-black text-white p-7 md:p-10 grid lg:grid-cols-2 gap-8 items-center"
          >
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-red mb-4">
                <Mailbox size={15} /> Parliamone
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tighter leading-tight">
                Per avere un supporto o per maggiori dettagli contatta il form.
              </h2>
              <p className="mt-4 text-white/75 text-sm md:text-base leading-relaxed">
                Compila il modulo: ti rispondiamo via email con disponibilità, condizioni e tutti i dettagli sul servizio di domiciliazione più adatto a te.
              </p>
              <div className="mt-5 flex items-center gap-2 text-sm text-white/60">
                <CheckCircle size={16} className="text-brand-red" /> {CONTACT.venueAddress}, {CONTACT.venueCity}
              </div>
            </div>
            <div className="lg:justify-self-end">
              <Link
                to="/contatti#contatti"
                data-testid="domiciliazione-form-cta"
                className="inline-flex items-center justify-center gap-2 bg-brand-red text-white text-sm font-bold uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-brand-black transition-colors"
              >
                <Mail size={18} /> Contatta il form
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
